
import { useState, useCallback, useRef, useEffect } from "react";
import { toast } from "sonner";
import { fetchPrompts } from "@/utils/exploreQueries";
import type { Prompt } from "@/services/promptService";

interface UsePromptLoadingProps {
  selectedCategory: string | null;
  currentPage: number;
  searchQuery: string;
  itemsPerPage: number;
  initialLoadComplete: React.MutableRefObject<boolean>;
  sortBy?: string;
  timeFilter?: string;
  onLoadComplete?: () => void;
}

export function usePromptLoading({
  selectedCategory,
  currentPage,
  searchQuery,
  itemsPerPage,
  initialLoadComplete,
  sortBy = 'relevance',
  timeFilter = '',
  onLoadComplete
}: UsePromptLoadingProps) {
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const isPromptLoading = useRef(false);
  const abortController = useRef<AbortController | null>(null);
  const lastRequestId = useRef<string>(Date.now().toString());
  const loadingTimeout = useRef<NodeJS.Timeout | null>(null);
  const loadingDelayTimer = useRef<NodeJS.Timeout | null>(null);
  const lastLoadTime = useRef<number>(0);
  const minLoadInterval = 1500; // Adjusted minimum time between loads

  // Load prompts with prevention of frequent loading
  const loadPrompts = useCallback(async () => {
    // Skip if already loading
    if (isPromptLoading.current) {
      console.log("Already loading prompts, skipping this request");
      return;
    }
    
    // Check if we've loaded recently - enforce minimum time between loads
    const now = Date.now();
    const timeSinceLastLoad = now - lastLoadTime.current;
    if (lastLoadTime.current > 0 && timeSinceLastLoad < minLoadInterval) {
      console.log(`Skipping load - too soon (${timeSinceLastLoad}ms since last load, minimum ${minLoadInterval}ms)`);
      return;
    }
    
    // Skip if search is active, handled by the search hook instead
    if (searchQuery) {
      console.log("Skipping prompt loading because search is active");
      setIsLoading(false);
      return;
    }
    
    // Generate a unique ID for this request to help track race conditions
    const requestId = Date.now().toString();
    lastRequestId.current = requestId;
    
    // Cancel any in-flight requests
    if (abortController.current) {
      console.log("Aborting previous prompt loading request");
      abortController.current.abort();
    }
    
    // Clear any pending timeout
    if (loadingTimeout.current) {
      clearTimeout(loadingTimeout.current);
    }
    
    if (loadingDelayTimer.current) {
      clearTimeout(loadingDelayTimer.current);
    }
    
    // Create new abort controller for this request
    abortController.current = new AbortController();
    
    isPromptLoading.current = true;
    setError(null);
    
    // Add a small delay before setting loading state to true
    // This prevents flickering for quick loads
    loadingDelayTimer.current = setTimeout(() => {
      if (isPromptLoading.current && requestId === lastRequestId.current) {
        setIsLoading(true);
      }
    }, 200); // Slightly reduced for faster feedback
    
    console.log(`Loading prompts - page: ${currentPage}, category: ${selectedCategory || "all"}, sort: ${sortBy}, timeFilter: ${timeFilter}, requestId: ${requestId}`);
    
    // Set a timeout to prevent getting stuck in loading state
    loadingTimeout.current = setTimeout(() => {
      if (isPromptLoading.current && requestId === lastRequestId.current) {
        console.log("Loading timeout reached, resetting loading state");
        setIsLoading(false);
        isPromptLoading.current = false;
        setError(new Error("Request timed out"));
        
        if (onLoadComplete) {
          onLoadComplete();
        }
      }
    }, 8000);
    
    try {
      // Update last load time
      lastLoadTime.current = now;
      
      // Pass sort and time filter parameters to fetchPrompts
      const data = await fetchPrompts(currentPage, itemsPerPage, selectedCategory, {
        sortBy,
        timeFilter
      });
      
      // Check if this is still the most recent request
      if (requestId !== lastRequestId.current) {
        console.log(`Ignoring stale prompt loading response for requestId: ${requestId}`);
        return;
      }
      
      console.log(`Loaded ${data.length} prompts for requestId: ${requestId}`);
      
      // Update prompts without any delay
      setPrompts(data);
      initialLoadComplete.current = true;
    } catch (error: any) {
      // Don't show error if it was caused by abort
      if (error.name !== 'AbortError') {
        console.error('Error loading prompts:', error);
        setError(error);
        if (prompts.length === 0) {
          toast.error('Failed to load prompts. Please try again.');
        }
      } else {
        console.log(`Prompt loading request ${requestId} was aborted`);
      }
      // Keep existing prompts on error
    } finally {
      // Clear timers
      if (loadingTimeout.current) {
        clearTimeout(loadingTimeout.current);
        loadingTimeout.current = null;
      }
      
      if (loadingDelayTimer.current) {
        clearTimeout(loadingDelayTimer.current);
        loadingDelayTimer.current = null;
      }
      
      // Only update loading state if this is still the current request
      if (requestId === lastRequestId.current) {
        // Add a delay before clearing loading state
        // This ensures that loading states don't flicker too quickly
        setTimeout(() => {
          console.log(`Finishing prompt loading for requestId: ${requestId}`);
          setIsLoading(false);
          isPromptLoading.current = false;
          
          if (onLoadComplete) {
            onLoadComplete();
          }
        }, 300); // Adjusted for smoother transitions
      }
    }
  }, [selectedCategory, currentPage, searchQuery, itemsPerPage, initialLoadComplete, prompts.length, onLoadComplete, sortBy, timeFilter]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      console.log("Cleaning up prompt loading abort controllers");
      if (abortController.current) {
        abortController.current.abort();
        abortController.current = null;
      }
      
      if (loadingTimeout.current) {
        clearTimeout(loadingTimeout.current);
        loadingTimeout.current = null;
      }
      
      if (loadingDelayTimer.current) {
        clearTimeout(loadingDelayTimer.current);
        loadingDelayTimer.current = null;
      }
    };
  }, []);

  return {
    prompts,
    isLoading,
    setIsLoading,
    loadPrompts,
    error
  };
}
